import axios from 'axios';
import {
  applyBackupPayload,
  assertMiniProgramBackupPayload,
  buildBackupPayload,
  countHoldingsInBackupPayload,
  MP_BACKUP_SCHEMA,
  parseBackup,
  stringifyBackup,
  validateBackupPayload
} from './assetStorageBackup';
import { getMpOpenid, maskMpOpenid } from './assetBackupIdentity';
import { decryptToPlaintext, encryptPlaintext } from './assetHoldingBackupCrypto';

function resolveHoldingBackupApi() {
  if (process.env.REACT_APP_HOLDING_BACKUP_API) {
    return process.env.REACT_APP_HOLDING_BACKUP_API;
  }
  // 生产环境直连腾讯云，避免经 oldox.vip Nginx 反代；源站宕机时 Cloudflare 会返回 521
  if (process.env.NODE_ENV === 'production') {
    return 'https://api.oldox.online/api/miniprogram/holding-backup';
  }
  return '/api/miniprogram/holding-backup';
}

const HOLDING_BACKUP_API = resolveHoldingBackupApi();

function resolveOpenid(openid) {
  return String(openid || getMpOpenid() || '').trim();
}

function buildBackupRequest(openid, body = {}) {
  const resolvedOpenid = resolveOpenid(openid);
  if (!resolvedOpenid) {
    return {
      ok: false,
      message: '请先填写并保存小程序 OpenID，以便与小程序共用云端备份'
    };
  }
  const headers = {};
  const token = localStorage.getItem('auth_token');
  if (token) headers.Authorization = `Bearer ${token}`;
  headers['X-Wx-Openid'] = resolvedOpenid;
  return {
    ok: true,
    openid: resolvedOpenid,
    config: { headers },
    body: {
      ...body,
      openid: resolvedOpenid
    }
  };
}

function formatRequestError(error) {
  const status = error.response?.status;
  if (status === 521) {
    return '网站源站暂时不可用（Cloudflare 521）。请刷新页面后重试；若仍失败，请稍后再试或检查境外服务器状态。';
  }
  if (status === 404) {
    return '备份 API 未找到（404）。本地 dev 请重启 npm run dev:local（需加载 setupProxy 转发到腾讯云）。';
  }
  if (error.code === 'ERR_NETWORK' || error.message === 'Network Error') {
    return '网络请求失败，请检查网络或 API 地址是否可达';
  }
  return error.message || '网络请求失败';
}

function unwrapResult(res) {
  const body = res?.data;
  if (!body || typeof body !== 'object') {
    return { ok: false, message: '服务响应异常', data: null };
  }
  if (Number(body.code) === 0) {
    return { ok: true, message: body.message || 'ok', data: body.data };
  }
  return { ok: false, message: body.message || '请求失败', code: body.code, data: body.data };
}

function noBackupMessage(openid) {
  const masked = maskMpOpenid(openid);
  return masked
    ? `云端暂无备份（OpenID ${masked}）。请确认与小程序一致，并已在小程序执行「云端备份」。`
    : '云端暂无备份，请先在小程序执行「云端备份」。';
}

export async function uploadLatestBackup(password, openid) {
  const req = buildBackupRequest(openid, { action: 'upload' });
  if (!req.ok) return req;

  const payload = buildBackupPayload();
  const preflight = assertMiniProgramBackupPayload(payload);
  if (!preflight.ok) return { ...preflight, phase: 'preflight' };

  const plaintext = stringifyBackup(payload, true);
  const enc = await encryptPlaintext(plaintext, password);
  if (!enc.ok) return { ...enc, phase: 'encrypt' };
  const envelope = {
    ...enc.envelope,
    backupSchema: payload.version,
    bytesPlain: plaintext.length
  };
  try {
    const res = await axios.post(HOLDING_BACKUP_API, {
      ...req.body,
      envelope
    }, req.config);
    const out = unwrapResult(res);
    if (!out.ok) {
      out.phase = 'upload';
      return out;
    }

    const verify = await loadAndDecryptBackup(password, openid);
    if (!verify.ok) {
      return {
        ok: false,
        phase: 'verify',
        message: verify.message || '上传成功但校验失败，请勿在小程序恢复，并联系支持'
      };
    }
    if (verify.backupSchema !== MP_BACKUP_SCHEMA) {
      return {
        ok: false,
        phase: 'verify',
        message: `云端备份格式仍为旧版（${verify.backupSchema || '未知'}），小程序无法恢复。请关闭所有 oldox.vip 标签页后重新打开，再执行一次云端备份。`
      };
    }
    return {
      ...out,
      message: `备份已保存（小程序兼容格式，${verify.holdingCount ?? 0} 条持仓）`
    };
  } catch (error) {
    return { ok: false, message: formatRequestError(error), phase: 'upload' };
  }
}

export async function fetchLatestBackupEnvelope(openid) {
  const req = buildBackupRequest(openid, { action: 'get' });
  if (!req.ok) return req;

  try {
    const res = await axios.post(HOLDING_BACKUP_API, req.body, req.config);
    return unwrapResult(res);
  } catch (error) {
    return { ok: false, message: formatRequestError(error) };
  }
}

async function loadAndDecryptBackup(password, openid) {
  const req = buildBackupRequest(openid);
  if (!req.ok) return req;

  const fetched = await fetchLatestBackupEnvelope(req.openid);
  if (!fetched.ok) {
    if (Number(fetched.code) === -3) {
      return { ok: false, message: noBackupMessage(req.openid), code: -3 };
    }
    return fetched;
  }
  const envelope = fetched.data?.envelope;
  if (!envelope) {
    return { ok: false, message: noBackupMessage(req.openid), code: -3 };
  }
  const dec = await decryptToPlaintext(envelope, password);
  if (!dec.ok) {
    return {
      ok: false,
      message: dec.message || '备份口令错误，无法解密云端数据',
      phase: 'decrypt'
    };
  }
  const parsed = parseBackup(dec.plaintext);
  if (!parsed.ok) return parsed;
  const valid = validateBackupPayload(parsed.payload);
  if (!valid.ok) return valid;
  return {
    ok: true,
    openid: req.openid,
    payload: parsed.payload,
    holdingCount: countHoldingsInBackupPayload(parsed.payload),
    updatedAt: envelope.updatedAt || null,
    backupSchema: parsed.payload?.schema || null
  };
}

export async function previewCloudBackupRestore(password, openid) {
  return loadAndDecryptBackup(password, openid);
}

export function applyCloudBackupPayload(payload) {
  const applied = applyBackupPayload(payload);
  if (!applied.ok) return applied;
  const holdingCount = applied.holdingCount ?? countHoldingsInBackupPayload(payload);
  return {
    ok: true,
    holdingCount,
    message: holdingCount > 0
      ? `已从云端恢复 ${holdingCount} 条持仓`
      : '云端备份中没有持仓数据'
  };
}

export async function fetchCloudBackupStatus(openid) {
  const req = buildBackupRequest(openid);
  if (!req.ok) return req;

  const fetched = await fetchLatestBackupEnvelope(req.openid);
  if (!fetched.ok) {
    if (Number(fetched.code) === -3) {
      return {
        ok: true,
        hasBackup: false,
        openid: req.openid,
        message: noBackupMessage(req.openid)
      };
    }
    return fetched;
  }
  const envelope = fetched.data?.envelope;
  return {
    ok: true,
    hasBackup: Boolean(envelope),
    openid: req.openid,
    updatedAt: envelope?.updatedAt || null,
    message: envelope ? '云端已有备份' : noBackupMessage(req.openid)
  };
}

export async function restoreLatestBackup(password, openid) {
  const loaded = await loadAndDecryptBackup(password, openid);
  if (!loaded.ok) return loaded;
  if (loaded.holdingCount === 0) {
    return { ok: false, message: '云端备份为空，没有可恢复的持仓' };
  }
  return applyCloudBackupPayload(loaded.payload);
}

export async function deleteCloudBackup(openid) {
  const req = buildBackupRequest(openid, { action: 'delete' });
  if (!req.ok) return req;

  try {
    const res = await axios.post(HOLDING_BACKUP_API, req.body, req.config);
    return unwrapResult(res);
  } catch (error) {
    return { ok: false, message: formatRequestError(error) };
  }
}

/** 网页登录用户自动备份：只走本机 /api/holding-backup，身份为 web_user_* */
const WEB_HOLDING_BACKUP_API = '/api/holding-backup';

function buildWebUserBackupRequest(body = {}) {
  const token = localStorage.getItem('auth_token');
  if (!token) {
    return { ok: false, message: '请先登录后再使用自动备份' };
  }
  return {
    ok: true,
    config: { headers: { Authorization: `Bearer ${token}` } },
    body: { ...body }
  };
}

async function postWebHoldingBackup(body) {
  const req = buildWebUserBackupRequest(body);
  if (!req.ok) return req;
  try {
    const res = await axios.post(WEB_HOLDING_BACKUP_API, req.body, req.config);
    return unwrapResult(res);
  } catch (error) {
    return { ok: false, message: formatRequestError(error) };
  }
}

export async function uploadWebUserBackup(password) {
  const req = buildWebUserBackupRequest({ action: 'upload' });
  if (!req.ok) return req;

  const payload = buildBackupPayload();
  const preflight = assertMiniProgramBackupPayload(payload);
  if (!preflight.ok) return { ...preflight, phase: 'preflight' };

  const plaintext = stringifyBackup(payload, true);
  const enc = await encryptPlaintext(plaintext, password);
  if (!enc.ok) return { ...enc, phase: 'encrypt' };
  const envelope = {
    ...enc.envelope,
    backupSchema: payload.version,
    bytesPlain: plaintext.length
  };

  const out = await postWebHoldingBackup({ action: 'upload', envelope });
  if (!out.ok) return { ...out, phase: 'upload' };

  const verify = await loadAndDecryptWebUserBackup(password);
  if (!verify.ok) {
    return {
      ok: false,
      phase: 'verify',
      message: verify.message || '上传成功但校验失败，请稍后重试'
    };
  }
  return {
    ...out,
    holdingCount: verify.holdingCount,
    updatedAt: out.data?.updatedAt || verify.updatedAt || null,
    message: `已备份到云端（账号备份，${verify.holdingCount ?? 0} 条持仓）`
  };
}

async function loadAndDecryptWebUserBackup(password) {
  const fetched = await postWebHoldingBackup({ action: 'get' });
  if (!fetched.ok) {
    if (Number(fetched.code) === -3) {
      return { ok: false, message: '云端暂无账号备份，请先执行一次备份', code: -3 };
    }
    return fetched;
  }
  const envelope = fetched.data?.envelope;
  if (!envelope) {
    return { ok: false, message: '云端暂无账号备份', code: -3 };
  }
  const dec = await decryptToPlaintext(envelope, password);
  if (!dec.ok) {
    return {
      ok: false,
      message: dec.message || '备份密码错误，无法解密',
      phase: 'decrypt'
    };
  }
  const parsed = parseBackup(dec.plaintext);
  if (!parsed.ok) return parsed;
  const valid = validateBackupPayload(parsed.payload);
  if (!valid.ok) return valid;
  return {
    ok: true,
    payload: parsed.payload,
    holdingCount: countHoldingsInBackupPayload(parsed.payload),
    updatedAt: envelope.updatedAt || null,
    backupSchema: parsed.payload?.schema || null
  };
}

export async function previewWebUserBackupRestore(password) {
  return loadAndDecryptWebUserBackup(password);
}

export async function restoreWebUserBackup(password) {
  const loaded = await loadAndDecryptWebUserBackup(password);
  if (!loaded.ok) return loaded;
  if (loaded.holdingCount === 0) {
    return { ok: false, message: '云端备份为空，没有可恢复的持仓' };
  }
  return applyCloudBackupPayload(loaded.payload);
}

export async function fetchWebUserBackupStatus() {
  const fetched = await postWebHoldingBackup({ action: 'get' });
  if (!fetched.ok) {
    if (Number(fetched.code) === -3) {
      return { ok: true, hasBackup: false, message: '云端暂无账号备份' };
    }
    return fetched;
  }
  const envelope = fetched.data?.envelope;
  return {
    ok: true,
    hasBackup: Boolean(envelope),
    updatedAt: envelope?.updatedAt || null,
    message: envelope ? '云端已有账号备份' : '云端暂无账号备份'
  };
}

export async function deleteWebUserBackup() {
  return postWebHoldingBackup({ action: 'delete' });
}

